// widget/src/hooks/usePageContext.ts
import { useEffect, useState } from "react"
import type { PageContext } from "../types"

interface Options {
  customerEmail?: string
  customerId?: string
}

const PAGES_KEY = "dyc-pearl-pages"

export function usePageContext(opts: Options = {}): PageContext {
  const [ctx, setCtx] = useState<PageContext>(() => snapshot(opts))

  // Refresh on navigation. Shopify themes with instant-page / client-side
  // routing change the URL via pushState (no popstate), so listening for
  // popstate/visibilitychange alone misses them — the page context (and the
  // product-page detection that drives the container dim) would stay stale.
  // A lightweight path-poll catches every navigation type reliably.
  useEffect(() => {
    const onChange = () => setCtx(snapshot(opts))
    window.addEventListener("popstate", onChange)
    document.addEventListener("visibilitychange", onChange)
    let lastPath = window.location.pathname
    const poll = window.setInterval(() => {
      if (window.location.pathname !== lastPath) {
        lastPath = window.location.pathname
        onChange()
      }
    }, 800)
    return () => {
      window.removeEventListener("popstate", onChange)
      document.removeEventListener("visibilitychange", onChange)
      window.clearInterval(poll)
    }
  }, [opts.customerEmail, opts.customerId])

  // ── Cart tracking ────────────────────────────────────────────────────
  //
  // Mount-time fetch establishes a baseline. After that, we monkeypatch
  // window.fetch to re-fetch `/cart.js` whenever the storefront hits one of
  // Shopify's cart-mutation endpoints (/cart/add.js, /cart/change.js, etc.).
  // Without this the dashboard's copilot sees an empty cart for the entire
  // session even after the customer adds items — Shopify themes don't emit a
  // universal cart-change event, so the fetch monkeypatch is the most reliable
  // signal. The Pearl widget is always loaded alongside the theme's own
  // ajax-cart calls, so we never miss one.
  useEffect(() => {
    let cancelled = false

    async function refreshCart() {
      try {
        const r = await fetch("/cart.js", { credentials: "include" })
        if (!r.ok) return
        const cart = (await r.json()) as {
          item_count?: number
          total_price?: number // in cents
          items?: Array<{
            variant_id?: number | string
            title: string
            quantity: number
            price?: number // in cents
            image?: string
          }>
        }
        if (cancelled) return
        const items = (cart.items ?? []).map((i) => ({
          variantId: String(i.variant_id ?? ""),
          title: i.title,
          quantity: i.quantity,
          price: typeof i.price === "number" ? i.price / 100 : 0,
          image: i.image,
        }))
        const summary = cart.item_count
          ? `${cart.item_count} item${cart.item_count === 1 ? "" : "s"}: ${items
              .slice(0, 3)
              .map((i) => `${i.title} ×${i.quantity}`)
              .join(", ")}`
          : "empty"
        const total =
          typeof cart.total_price === "number" ? cart.total_price / 100 : 0
        setCtx((prev) => ({
          ...prev,
          cartSummary: summary,
          cartItems: items,
          cartTotal: total,
        }))
      } catch {
        // not on Shopify or cart endpoint missing — ignore
      }
    }

    refreshCart() // initial baseline

    // Fetch monkeypatch: any cart-mutation URL triggers a re-fetch after the
    // mutation lands. We attach to the SUCCESS side (after the response
    // resolves) so /cart.js reflects the post-mutation state.
    if (typeof window === "undefined") return () => {}
    const origFetch = window.fetch.bind(window)

    const CART_WRITES = /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#].*)?$/

    function patchedFetch(
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input instanceof Request
              ? input.url
              : ""
      const isCartWrite = CART_WRITES.test(url)
      const p = origFetch(input, init)
      if (isCartWrite) {
        // Don't block the caller; refresh on the next tick so /cart.js
        // serves the updated state.
        p.then(() => refreshCart()).catch(() => {})
      }
      return p
    }

    window.fetch = patchedFetch as typeof window.fetch

    return () => {
      cancelled = true
      // Restore original fetch only if we still own the slot (some other
      // wrapper may have patched after us — leave it alone in that case).
      if (window.fetch === patchedFetch) window.fetch = origFetch
    }
  }, [])

  return ctx
}

function snapshot(opts: Options): PageContext {
  if (typeof window === "undefined") return {}
  const path = window.location.pathname
  const productMatch = path.match(/\/products\/([^/?#]+)/)
  return {
    currentPage: path,
    productHandle: productMatch?.[1],
    productTitle: productMatch ? extractProductTitle() : undefined,
    device: window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop",
    customerEmail: opts.customerEmail,
    customerId: opts.customerId,
    pagesViewed: readPagesViewed(),
  }
}

/**
 * Best-effort product name extraction from the page. Shopify themes vary
 * but the conventions we can rely on (in order):
 *   1. <meta property="og:title"> — most reliable, set by every modern
 *      theme on product pages
 *   2. document.title minus the store-name suffix (split on the common
 *      separators – , — , | , ·) — works for stock Dawn / Debut / Crave
 *   3. <h1> text — fallback when meta tags are wrong
 *
 * Returns undefined if nothing usable found, so the widget can fall back
 * to its handle-based or fully-generic opener cleanly.
 */
function extractProductTitle(): string | undefined {
  if (typeof document === "undefined") return undefined
  // Order matters. Shopify themes pack SEO subtitles into both og:title
  // and document.title (e.g. "Abyss Midnight Flip Car Kit - Peelable Car
  // Paint You Can Cool Off With"). The <h1> is almost always the clean
  // product name only, so we try it FIRST. Previous version pulled og:title
  // raw and that produced grotesque truncations like "...You Can Cou" when
  // sliced to 60 chars in the intro line.
  const h1 = document
    .querySelector<HTMLHeadingElement>("h1")
    ?.textContent?.trim()
  if (h1 && h1.length <= 80) return cleanProductTitle(h1)

  // og:title fallback — clean SEO subtitles by trimming at the first " - ",
  // " – ", " — ", " | ", " · " separator. The leftmost chunk is the
  // product name; everything after is store name / SEO description.
  const og = document
    .querySelector<HTMLMetaElement>('meta[property="og:title"]')
    ?.content?.trim()
  if (og) {
    const cleaned = cleanProductTitle(og)
    if (cleaned) return cleaned
  }

  // document.title with the same SEO subtitle treatment.
  const raw = document.title?.trim() || ""
  if (raw) {
    const cleaned = cleanProductTitle(raw)
    if (cleaned) return cleaned
  }
  return undefined
}

/**
 * Strip Shopify-style SEO subtitles + store suffixes. Splits on the first
 * occurrence of any common separator (with spaces around) and keeps the
 * leftmost chunk — that's the product name. Examples:
 *   "Abyss Midnight Flip Car Kit - Peelable Car Paint You Can Cool Off With"
 *     → "Abyss Midnight Flip Car Kit"
 *   "Anthias ColorShift Car Kit | DipYourCar"
 *     → "Anthias ColorShift Car Kit"
 *   "Black Emerald Metallic Wheel Kit – HyperDip"
 *     → "Black Emerald Metallic Wheel Kit"
 */
function cleanProductTitle(s: string): string | undefined {
  const trimmed = s.trim()
  if (!trimmed) return undefined
  const first = trimmed.split(/\s+[-–—|·]\s+/)[0]?.trim()
  if (!first) return undefined
  // Guard against the rare case where the FIRST segment is super short
  // (e.g. "DYC | Anthias ColorShift Car Kit") — fall back to the longest
  // segment in that case.
  if (first.length < 6) {
    const parts = trimmed.split(/\s+[-–—|·]\s+/).map((p) => p.trim())
    const longest = parts.reduce((a, b) => (b.length > a.length ? b : a))
    return longest || first
  }
  return first
}

function readPagesViewed(): string[] {
  try {
    const raw = sessionStorage.getItem(PAGES_KEY)
    const arr = raw ? (JSON.parse(raw) as string[]) : []
    const here = window.location.pathname
    if (arr[arr.length - 1] !== here) {
      arr.push(here)
      while (arr.length > 5) arr.shift()
      sessionStorage.setItem(PAGES_KEY, JSON.stringify(arr))
    }
    return arr
  } catch {
    return [window.location.pathname]
  }
}
